import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, BarChart3, ShoppingCart, Check } from "lucide-react";
import { toast } from "sonner";
import { useCourses } from "@/lib/useCourses";
import { useCart, formatInr } from "@/lib/cart";
import { PRICING, usePricing } from "@/lib/pricing";

export default function CoursesIncluded() {
  usePricing();
  const courses = useCourses();
  const { has, toggleCourse, cart } = useCart();
  const navigate = useNavigate();

  const goToCourse = (slug) => navigate(`/courses/${slug}`);

  const onAddToCart = (e, slug, title) => {
    e.stopPropagation();
    const wasAdded = has(slug);
    if (!wasAdded && cart.n >= PRICING.maxCartCourses) {
      toast.error(`Cart already has ${PRICING.maxCartCourses} courses (max).`);
      return;
    }
    toggleCourse(slug);
    if (!wasAdded) toast.success(`Added "${title}" to cart`);
    else toast(`Removed "${title}"`);
  };

  return (
    <section
      id="courses"
      data-testid="section-courses-included"
      className="py-14 sm:py-20 bg-gradient-to-b from-transparent to-slate-100/60 dark:to-white/[0.02]"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="text-center max-w-3xl mx-auto">
          <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
            Inside the Academy
          </span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl tracking-tighter font-semibold text-[#0F172A] dark:text-white text-balance">
            Courses Included In The Academy
          </h2>
          <p className="mt-5 text-slate-600 dark:text-slate-300">
            Each course is {formatInr(PRICING.coursePrice)}. Add 2 to auto-unlock <strong>E-Lite</strong>, 5 for <strong>Basic</strong>, all {PRICING.totalCourses} to unlock <strong>Premium</strong>.
          </p>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.06 } } }}
          className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {courses.map((c, i) => {
            const Icon = c.icon;
            const added = has(c.slug);
            return (
              <motion.div
                key={c.slug}
                role="button"
                tabIndex={0}
                data-testid={`included-card-${c.slug}`}
                onClick={() => goToCourse(c.slug)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    goToCourse(c.slug);
                  }
                }}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
                }}
                whileHover={{ y: -4 }}
                transition={{ type: "spring", stiffness: 220, damping: 22 }}
                className={`group cursor-pointer rounded-3xl border bg-white dark:bg-white/[0.04] overflow-hidden hairline flex flex-col transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#06B6D4]/70 ${
                  added
                    ? "border-[#06B6D4]/70 shadow-[0_15px_40px_-15px_rgba(6,182,212,0.45)] hover:shadow-[0_25px_60px_-15px_rgba(6,182,212,0.6)]"
                    : "border-slate-200/80 dark:border-white/10 hover:border-[#06B6D4]/50 hover:shadow-[0_25px_60px_-20px_rgba(15,23,42,0.25)] dark:hover:shadow-[0_25px_60px_-20px_rgba(6,182,212,0.35)]"
                }`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={c.cover}
                    alt={c.title}
                    loading={i < 3 ? "eager" : "lazy"}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#020617]/85 via-[#020617]/20 to-transparent" />
                  <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-white/15 backdrop-blur border border-white/20 px-2.5 py-1 text-[10px] uppercase tracking-[0.2em] text-white font-semibold">
                    <Icon className="h-3 w-3" /> Mastery
                  </div>
                  <div className="absolute top-4 right-4 inline-flex items-center rounded-full bg-white/15 backdrop-blur border border-white/20 px-3 py-1 text-xs text-white font-semibold">
                    {formatInr(PRICING.coursePrice)}
                  </div>
                  <div className="absolute bottom-4 right-4 h-9 w-9 inline-flex items-center justify-center rounded-full bg-white/90 text-[#0F172A] opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300 shadow-lg">
                    <ArrowRight className="h-4 w-4" />
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <h3 className="font-display text-lg font-medium tracking-tight text-[#0F172A] dark:text-white leading-snug group-hover:text-[#06B6D4] transition-colors duration-300">
                    {c.title}
                  </h3>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                    {c.short}
                  </p>
                  <div className="mt-4 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
                    <span className="inline-flex items-center gap-1">
                      <Clock className="h-3 w-3" /> {c.duration}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <BarChart3 className="h-3 w-3" /> {c.level}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => onAddToCart(e, c.slug, c.title)}
                    data-testid={`add-to-cart-${c.slug}`}
                    aria-label={added ? `Remove ${c.title}` : `Add ${c.title} to cart`}
                    className={`mt-6 w-full inline-flex items-center justify-center gap-1.5 rounded-full px-4 py-2.5 text-sm font-semibold transition-all duration-300 ${
                      added
                        ? "bg-emerald-500 text-white hover:bg-emerald-600"
                        : "bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] hover:-translate-y-0.5"
                    }`}
                  >
                    {added ? (
                      <>
                        <Check className="h-4 w-4" /> Added — Remove
                      </>
                    ) : (
                      <>
                        <ShoppingCart className="h-4 w-4" /> Add to Cart
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="mt-12 text-center">
          <a
            href="#packages"
            data-testid="included-choose-package"
            className="inline-flex items-center gap-2 rounded-full bg-[#0F172A] dark:bg-white text-white dark:text-[#0F172A] px-6 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform"
          >
            See Package Pricing <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </div>
    </section>
  );
}
